import { useState, useEffect } from 'react';
import { api } from '../api/client';
import { useAuth } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, RefreshCw, Trash2, Key, Copy, Check } from 'lucide-react';

interface ApiKey {
  id: string;
  keyPrefix: string;
  project_id: string;
  project_name: string;
  name: string;
  permissions: string[];
  rate_limit: number;
  daily_limit: number;
  isActive: boolean;
  created_at: string;
  last_used_at: string | null;
}

interface Project {
  id: string;
  name: string;
}

function ApiKeysContent({ showHeader = true }: { showHeader?: boolean }) {
  const { user } = useAuth();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({
    projectId: '',
    name: '',
    permissions: ['verify'] as string[],
    rateLimit: 100,
    dailyLimit: 10000,
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const canManage = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [keysData, projectsData] = await Promise.all([
        api.getApiKeys(),
        api.getProjects(),
      ]);
      setApiKeys(keysData.apiKeys);
      setProjects(projectsData.projects);
    } catch {
      // Silently fail
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const result = await api.createApiKey(formData);
      setNewKey(result.key);
      fetchData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Operation failed';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (key: ApiKey) => {
    try {
      await api.updateApiKey(key.id, { isActive: !key.isActive });
      fetchData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Update failed';
      alert(message);
    }
  };

  const handleRegenerate = async (id: string) => {
    if (!confirm('Regenerate this API key? The old key will stop working immediately.')) return;
    try {
      const result = await api.regenerateApiKey(id);
      setNewKey(result.key);
      setShowModal(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Regenerate failed';
      alert(message);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this API key?')) return;
    try {
      await api.deleteApiKey(id);
      fetchData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Delete failed';
      alert(message);
    }
  };

  const copyToClipboard = async () => {
    if (newKey) {
      await navigator.clipboard.writeText(newKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const openCreateModal = () => {
    setFormData({
      projectId: projects[0]?.id || '',
      name: '',
      permissions: ['verify'],
      rateLimit: 100,
      dailyLimit: 10000,
    });
    setNewKey(null);
    setError('');
    setShowModal(true);
  };

  const togglePermission = (perm: string) => {
    if (formData.permissions.includes(perm)) {
      setFormData({
        ...formData,
        permissions: formData.permissions.filter((p) => p !== perm),
      });
    } else {
      setFormData({
        ...formData,
        permissions: [...formData.permissions, perm],
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {showHeader && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">API Keys</h1>
            <p className="text-muted-foreground">Manage API keys for your projects</p>
          </div>
          {canManage && (
            <Button onClick={openCreateModal}>
              <Plus className="mr-2 h-4 w-4" />
              New API Key
            </Button>
          )}
        </div>
      )}

      {newKey && (
        <Card className="border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Check className="h-5 w-5 text-emerald-600" />
              <span className="font-semibold text-emerald-800 dark:text-emerald-200">
                API Key Generated
              </span>
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded bg-white dark:bg-black p-3 font-mono text-sm break-all">
                {newKey}
              </code>
              <Button variant="outline" size="icon" onClick={copyToClipboard}>
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <p className="mt-2 text-sm text-emerald-700 dark:text-emerald-300">
              Save this key now. You won't be able to see it again.
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>All API Keys</CardTitle>
            {!showHeader && canManage && (
              <Button onClick={openCreateModal}>
                <Plus className="mr-2 h-4 w-4" />
                New API Key
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {apiKeys.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Key className="h-12 w-12 text-muted-foreground/50" />
              <p className="mt-4 text-muted-foreground">No API keys yet</p>
              {canManage && (
                <Button onClick={openCreateModal} className="mt-4" variant="outline">
                  <Plus className="mr-2 h-4 w-4" />
                  Create your first API key
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Key</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Permissions</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Used</TableHead>
                  {canManage && <TableHead className="w-[150px]">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {apiKeys.map((key) => (
                  <TableRow key={key.id}>
                    <TableCell className="font-medium">{key.name}</TableCell>
                    <TableCell className="font-mono text-sm">{key.keyPrefix}...</TableCell>
                    <TableCell>{key.project_name}</TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {key.permissions.map((p) => (
                          <Badge key={p} variant="secondary">
                            {p}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={key.isActive ? 'success' : 'destructive'}>
                        {key.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {key.last_used_at ? new Date(key.last_used_at).toLocaleString() : 'Never'}
                    </TableCell>
                    {canManage && (
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleActive(key)}
                          >
                            {key.isActive ? 'Disable' : 'Enable'}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRegenerate(key.id)}
                          >
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(key.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showModal && !newKey} onOpenChange={(open) => !newKey && setShowModal(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New API Key</DialogTitle>
            <DialogDescription>
              Create a new API key for accessing the verification API
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Project</Label>
                <Select
                  value={formData.projectId}
                  onValueChange={(value) => setFormData({ ...formData, projectId: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="keyName">Name</Label>
                <Input
                  id="keyName"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Production API Key"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Permissions</Label>
                <div className="flex gap-4">
                  {['verify', 'batch', 'stats'].map((perm) => (
                    <div key={perm} className="flex items-center gap-2">
                      <Checkbox
                        id={perm}
                        checked={formData.permissions.includes(perm)}
                        onCheckedChange={() => togglePermission(perm)}
                      />
                      <Label htmlFor={perm} className="font-normal">
                        {perm}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="rateLimit">Rate Limit (req/min)</Label>
                  <Input
                    id="rateLimit"
                    type="number"
                    value={formData.rateLimit}
                    onChange={(e) => setFormData({ ...formData, rateLimit: parseInt(e.target.value) })}
                    min={1}
                    max={10000}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dailyLimit">Daily Limit</Label>
                  <Input
                    id="dailyLimit"
                    type="number"
                    value={formData.dailyLimit}
                    onChange={(e) => setFormData({ ...formData, dailyLimit: parseInt(e.target.value) })}
                    min={1}
                    max={1000000}
                  />
                </div>
              </div>
              {error && <div className="text-sm text-destructive">{error}</div>}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Creating...' : 'Create API Key'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function ApiKeysTab() {
  return <ApiKeysContent showHeader={false} />;
}

export default function ApiKeys() {
  return <ApiKeysContent />;
}
